import { useCallback, useEffect, useMemo, useState } from 'react'
import useOnboardingData from 'hooks/useUser/useOnboardingData'
import useAppointmentTypeId from 'hooks/useAppointments/useAppointmentTypeId'
import { useSchedulingContext } from 'context/SchedulingContext/SchedulingProvider'
import { useSourceContext } from 'context/SourceContext/SourceProvider'
import type {
  ConditionTagMapping,
  ProviderTags,
} from 'containers/Scheduling/types'
import { useFlags } from 'launchdarkly-react-client-sdk'

export function useProviderMatchingEligibility() {
  const {
    appointmentType,
    isReschedule,
    rebookingFlowData,
  } = useSchedulingContext()
  const { patientProviderMatchingEnabled } = useFlags()
  const { appointmentTypeId: initialMdAppointmentType } = useAppointmentTypeId(
    'Initial MD'
  )
  const {
    onboardingData,
    isLoadingOnboardingData,
    onboardingDataError,
  } = useOnboardingData()

  const onboardingCondition = useMemo(() => {
    if (
      isLoadingOnboardingData ||
      onboardingDataError ||
      !onboardingData ||
      !onboardingData.conditions.length
    ) {
      return null
    }

    return onboardingData.conditions[0].value
  }, [onboardingData, isLoadingOnboardingData, onboardingDataError])

  const isInitialMdAppointmentType = useMemo(() => {
    return appointmentType?.id
      ? appointmentType.id === initialMdAppointmentType
      : false
  }, [appointmentType, initialMdAppointmentType])

  /*
   * Currently, we only support provider matching for Initial MD appointments.
   * We intentionally exclude reschedules/rebooks as well, for now.
   */
  const isProviderMatchingEnabled = useMemo(() => {
    return (
      patientProviderMatchingEnabled &&
      Boolean(onboardingCondition) &&
      isInitialMdAppointmentType &&
      !isReschedule &&
      !rebookingFlowData
    )
  }, [
    onboardingCondition,
    isInitialMdAppointmentType,
    isReschedule,
    rebookingFlowData,
  ])

  return {
    isProviderMatchingEnabled,
    onboardingCondition,
    initialMdAppointmentType,
    isInitialMdAppointmentType,
  }
}

export function useProviderMatchingData(onboardingCondition: string | null) {
  const { fetchSourceEndpoint } = useSourceContext()
  const [
    conditionTagMappings,
    setConditionTagMappings,
  ] = useState<ConditionTagMapping | null>(null)
  const [conditionDisplayName, setConditionDisplayName] = useState<
    string | null
  >(null)
  const [
    conditionProviderMatchDescription,
    setConditionProviderMatchDescription,
  ] = useState<string | null>(null)

  const fetchProviderTags = useCallback(
    async (providerIds: string[]) => {
      if (providerIds.length === 0) {
        return []
      }

      const endpoint = `provider-tags?${providerIds
        .map((id) => `provider_ids[]=${id}`)
        .join('&')}`
      const data = await fetchSourceEndpoint<ProviderTags[]>(endpoint)
      return data
    },
    [fetchSourceEndpoint]
  )

  const fetchConditionTagMappings = useCallback(async () => {
    const endpoint = `provider-tags/condition-mappings?condition_code=${onboardingCondition}`
    const data = await fetchSourceEndpoint<ConditionTagMapping>(endpoint)
    setConditionTagMappings(data)

    if (data?.condition_display_name) {
      setConditionDisplayName(data.condition_display_name)
    }

    if (data?.condition_provider_match_description) {
      setConditionProviderMatchDescription(
        data.condition_provider_match_description
      )
    }

    return data
  }, [fetchSourceEndpoint, onboardingCondition])

  useEffect(() => {
    if (!onboardingCondition) {
      return
    }

    fetchConditionTagMappings()
  }, [onboardingCondition, fetchConditionTagMappings])

  return {
    conditionTagMappings,
    conditionDisplayName,
    conditionProviderMatchDescription,
    fetchConditionTagMappings,
    fetchProviderTags,
  }
}
